import { looks as rules } from "@app/domain";
import type { Access, Look } from "@app/protocol";
import { useT } from "../i18n/context";
import { trialLeft } from "./trialLeft";
import { useOnce } from "./useOnce";

function Ribbon({ access, skew }: { access: Access; skew: number }) {
  const { t } = useT();
  if (access.kind === "available") return <>{t("club.trialOffer")}</>;
  if (access.kind === "ended") return <>{t("club.locked")}</>;
  if (access.kind !== "trial") return null;
  const left = trialLeft(access.endsAt - (Date.now() + skew));
  return <>{t(`club.left.${left.unit}`, { count: left.count })}</>;
}

/** The marks over a Club look's picture: Club, "New", and the trial ribbon (review 0015 S9). */
export function LookMarks({
  look,
  access,
  skew,
  ribbonId,
  isNew,
  starting,
}: {
  look: Look;
  access: Access | undefined;
  skew: number;
  ribbonId: string;
  /** A look worth the "New" mark to someone who never tried it. */
  isNew: boolean;
  /** Its trial is being recorded: the ribbon says so until the table may use it. */
  starting: boolean;
}) {
  const { t } = useT();
  // "New" only to someone who never tried it; a trial they ran is not news.
  const fresh = useOnce(
    `hcc:look-seen:${rules.lookKey(look)}`,
    isNew && access?.kind === "available",
  );
  if (!rules.isClub(look)) return null;
  return (
    <>
      <span className="absolute top-2 left-2 rounded-pill bg-gold px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-gold-ink">
        {t("club.badge")}
      </span>
      {fresh && (
        <span className="absolute top-2 right-2 flex items-center gap-1 rounded-pill bg-danger px-2 py-0.5 text-[11px] font-bold text-ink">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-ink" />
          {t("club.new")}
        </span>
      )}
      {access !== undefined && access.kind !== "club" && (
        <span
          id={ribbonId}
          className="absolute inset-x-0 bottom-9 bg-veil px-2 py-1 text-center text-[12px] font-bold text-ink"
        >
          {starting ? t("club.starting") : <Ribbon access={access} skew={skew} />}
        </span>
      )}
    </>
  );
}
